import { useState } from 'react'
import { users as api } from '@/api/endpoints'
import type { Role, Team } from '@/api/types'
import { Err } from '@/components/Err'
import { Button } from '@/components/ui/button'
import { DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { roleLabel } from '@/domain/words'
import { usePicked } from '@/hooks/usePicked'
import { useSession } from '@/hooks/useSession'
import { personRow } from '@/views/model'
import { LIST, PeoplePicker, ROW } from './PeoplePicker'
import type { Candidate } from './teamPick'

// `권한`(F-81) — 여러 사람에게 등급 하나를 준다. 대화 상자의 본문만이고 `<Dialog>` 는 `UserActions` 가 갖는다.
// 왼쪽 칸은 `직위` 와 같은 `PeoplePicker` 이고 줄마다 지금 권한이 선다. 권한은 찾는 말이 아니다 — 등급은 사용자 목록의 구획이다.
// **자기 자신은 후보에 없다** — 자기 등급은 내 정보에서 내려놓기만 한다(#311). 최고 관리자끼리는 서로 바꾼다(F-79).
// **최고 관리자로 올리는 사람은 아이디를 한 사람씩 다시 적는다**(F-79) — 올린 사람은 나를 포함한 모두를 바꿀 수 있다.
// 이미 그 등급인 사람에게는 보내지 않는다. 실패한 사람만 남고 같은 버튼이 곧 재시도다(직위 창과 같다).
// **바뀔 사람은 적용 직전 그 사람을 다시 읽어 정한다** — 쥔 줄은 고른 순간의 것이고 찾기 밖에 있으면 새로 읽히지 않아,
// 그 사이 다른 최고 관리자가 바꾼 사람을 이미 그 등급이라 보고 건너뛴다. 쥔 줄로는 이미 최고 관리자라 아이디를 묻지 않은
// 사람이 지금은 아니면 올리지 않고 남긴다 — 다시 읽은 줄로 바뀌어 그 사람의 아이디 칸이 선다.
// 다시 읽은 사람이 탈퇴했으면 보내지 않고 고른 칸에서 뺀 뒤 한 줄로 말한다 — 서버는 그 쓰기를 409 `already_withdrawn` 으로
// 거절하고, 고르는 자리의 찾기에 없는 사람이라 남겨 두면 풀 길 없이 적용마다 같은 거절이다.

const ROLES: Role[] = ['superadmin', 'admin', 'user']

const ROLE_RESULT: Record<Role, string> = {
  superadmin: '최고 관리자는 모든 사용자와 VM 을 봅니다.',
  admin: '관리자는 모든 사용자와 자기가 관리하는 팀의 VM 을 봅니다.',
  user: '사용자는 자기 VM 만 봅니다.',
}

export function RolePanel({ seed, teams, onDone }: { seed: Candidate[]; teams: Team[]; onDone: () => void }) {
  const me = useSession()
  const who = usePicked(seed)
  const [role, setRole] = useState<Role | null>(null)
  const [typed, setTyped] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [dropped, setDropped] = useState<string[]>([])

  const chosen = who.chosen
  const typedFor = (id: string, username: string) => (typed[id] ?? '').trim() === username
  const raising = role === 'superadmin' ? chosen.filter((p) => p.user.role !== 'superadmin') : []
  const confirmed = raising.every((p) => typedFor(p.user.id, p.user.username))

  const save = async () => {
    if (role === null || !confirmed) return
    setBusy(true)
    setError(null)
    setDropped([])
    let first: unknown = null
    const failed = new Set<string>()
    const gone: string[] = []
    const read: Candidate[] = []
    for (const p of chosen) {
      try {
        const now = await api.get(p.user.id)
        if (now.withdrawn === true) {
          gone.push(p.user.username)
          continue
        }
        read.push(personRow(now, teams))
        // 바뀌는 사람만 부른다 — 이미 그 등급인 사람은 할 일이 없다.
        if (now.role === role) continue
        if (role === 'superadmin' && !typedFor(now.id, now.username)) {
          failed.add(p.user.id)
          continue
        }
        await api.setRole(p.user.id, role)
      } catch (e) {
        if (first === null) first = e
        failed.add(p.user.id)
      }
    }
    setBusy(false)
    who.renew(read)
    if (failed.size > 0 || gone.length > 0) {
      setError(first)
      setDropped(gone)
      who.only(failed)
      return
    }
    onDone()
  }

  const ready = chosen.length > 0 && role !== null && confirmed

  return (
    <>
      <DialogHeader>
        <DialogTitle>권한</DialogTitle>
      </DialogHeader>

      <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
        <PeoplePicker
          id="role-users"
          teams={teams}
          chosen={chosen}
          onMember={who.set}
          onFound={who.renew}
          omit={(p) => p.user.id === me.id}
          aside={(p) => roleLabel[p.user.role]}
        />

        <div className="grid content-start gap-2">
          <Label id="role-pick">권한</Label>
          <ul aria-labelledby="role-pick" className="rounded-md border">
            {ROLES.map((r) => (
              <li key={r}>
                <label className={`${ROW} items-start`}>
                  <input
                    type="radio"
                    name="role-pick"
                    aria-label={`${roleLabel[r]} 고르기`}
                    className="mt-1 shrink-0"
                    value={r}
                    checked={role === r}
                    disabled={busy}
                    onChange={() => setRole(r)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium">{roleLabel[r]}</span>
                    <span className="mt-0.5 block text-[11px] text-muted-foreground">{ROLE_RESULT[r]}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
          {raising.length > 0 ? (
            <div className="grid gap-1.5">
              <Label id="role-confirm">아이디 확인</Label>
              <ul aria-labelledby="role-confirm" className={`${LIST} h-auto max-h-48 max-sm:h-auto`}>
                {raising.map((p) => (
                  <li key={p.user.id} className="px-3 py-1.5">
                    <Input
                      value={typed[p.user.id] ?? ''}
                      placeholder={p.user.username}
                      aria-label={`${p.user.username} 아이디 확인`}
                      autoComplete="off"
                      disabled={busy}
                      onChange={(e) => setTyped((v) => ({ ...v, [p.user.id]: e.target.value }))}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="flex justify-end">
            <Button type="button" size="sm" disabled={!ready || busy} onClick={() => void save()}>
              {busy ? '적용 중' : '적용'}
            </Button>
          </div>
        </div>
      </div>

      {dropped.length > 0 ? (
        <p role="status" className="text-[13px] text-warn-ink">{`탈퇴한 ${dropped.join(', ')} 의 고르기를 풀었습니다.`}</p>
      ) : null}
      <Err error={error} />
      <DialogFooter>
        <Button type="button" variant="outline" disabled={busy} onClick={onDone}>
          닫기
        </Button>
      </DialogFooter>
    </>
  )
}
